import React, { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "react-table";
import Box from "@mui/material/Box";
import { useHasRole } from "@homesusa/auth";
import {
  Format,
  PersistentRightModal,
  PersistentRightModalContext,
  RoleEmployee,
  StatusIndicatorIcon
} from "@homesusa/core";
import { FormContext } from "@homesusa/form";
import {
  GridTable,
  GridProvider,
  GridToolbar,
  FetchDataDownloading,
  GridResponse,
  useFetchData
} from "@homesusa/grid-table";
import {
  AppContext,
  HeaderOutlet,
  HeaderTitle,
  SubmitButton,
  useTheme
} from "@homesusa/layout";
import { UserStatus, UserStatusLabel, UserStatusColor } from "core/enums";
import { UserDetail } from "core/interfaces";
import { getUsers, createUser } from "core/services/user.service";
import { UserCreateForm } from "../components";
import { UserCreateFormProvider } from "../providers";

function SaveButton({
  getUsers
}: {
  getUsers: (props: { pageSize: number; pageIndex: number }) => void;
}): JSX.Element {
  const { addAlert } = React.useContext(AppContext);
  const { formState } = React.useContext(FormContext);
  const { setShowRightModal } = React.useContext(PersistentRightModalContext);

  return (
    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
      <SubmitButton
        onClick={async (): Promise<void> =>
          createUser(formState).then((): void => {
            addAlert({
              message: "The request was successfully created",
              variant: "success"
            });
            getUsers({ pageSize: 50, pageIndex: 0 });
            setShowRightModal(false);
          })
        }
      >
        Save
      </SubmitButton>
    </Box>
  );
}

function UserGrid(): JSX.Element {
  const { isMlsAdministrator, hasEmployeeRole } = useHasRole();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const { variant } = useTheme();
  const defaultSortBy = "+firstName,+lastName";
  const columns: Array<Column<UserDetail>> = React.useMemo(
    () => [
      {
        Header: "First Name",
        accessor: (data: UserDetail): string => data.firstName
      },
      {
        Header: "Last Name",
        accessor: (data: UserDetail): string => data.lastName
      },
      {
        Header: "Username",
        accessor: (data: UserDetail): string => data.email,
        id: "email"
      },
      {
        Header: "Email Confirmed",
        accessor: (data: UserDetail): string =>
          data.emailConfirmed ? "Yes" : "No"
      },
      {
        Header: "Business Phone",
        accessor: (data: UserDetail): string =>
          Format.PhoneNumber(data.businessPhone) ?? "",
        disableSortBy: true
      },
      {
        Header: "Mobile Phone",
        accessor: (data: UserDetail): string =>
          Format.PhoneNumber(data.mobilePhone) ?? "",
        disableSortBy: true
      },
      {
        Header: "Created",
        accessor: (data: UserDetail): string =>
          Format.DateTime(data.sysCreatedOn),
        id: "sysCreatedOn"
      },
      {
        Header: "Last Activity",
        accessor: (data: UserDetail): string => Format.DateTime(data.lastLogin),
        id: "lastLogin"
      },
      {
        Header: "Role",
        accessor: (data: UserDetail): string => data.roles.toString(),
        id: "roleName"
      }
    ],
    []
  );
  const [userStatus, setUserStatus] = React.useState<UserStatus>(
    Object.values(UserStatus).slice(0, 1)[0]
  );

  const getUser = ({ row }: { row: UserDetail }): void => {
    navigate(`/users/${row.id}`);
  };

  const getGridData = React.useCallback(
    async ({
      pageSize,
      pageIndex,
      sortBy,
      globalFilter,
      isForDownloading
    }: FetchDataDownloading): Promise<GridResponse<UserDetail>> => {
      return getUsers({
        top: pageSize,
        skip: pageIndex,
        searchBy: globalFilter,
        sortBy: sortBy ?? defaultSortBy,
        isForDownloading,
        status: userStatus
      });
    },
    [userStatus]
  );
  const { data, fetchData, fetchDataForDownloading } =
    useFetchData<UserDetail>(getGridData);

  const statusOptions = React.useMemo(() => {
    const options = Object.fromEntries(
      Object.values(UserStatus).map((status) => [
        status.toString(),
        UserStatusLabel.get(status) ?? ""
      ])
    );

    return hasEmployeeRole([RoleEmployee.CompanyAdmin])
      ? Object.fromEntries(
          Object.entries(options).filter(
            ([key]): boolean => key !== UserStatus.Archived.toString()
          )
        )
      : options;
  }, [hasEmployeeRole]);

  const headerTitle = <HeaderTitle title="Users" />;
  return (
    <Fragment>
      <GridProvider
        options={{ columns, data: data.data }}
        totalRows={data.total}
        fetchData={fetchData}
      >
        <HeaderOutlet>{headerTitle}</HeaderOutlet>
        <GridToolbar title={variant === "old" && headerTitle}>
          {variant === "new" && (
            <GridToolbar.Left>
              {(isMlsAdministrator ||
                hasEmployeeRole([RoleEmployee.CompanyAdmin])) && (
                <GridToolbar.SelectFilter
                  defaultValue={UserStatus.Active}
                  onChange={(value): void => setUserStatus(value as UserStatus)}
                  options={statusOptions}
                  label="Status"
                  startIcon={<StatusIndicatorIcon />}
                  statusColor={UserStatusColor}
                />
              )}
            </GridToolbar.Left>
          )}
          <GridToolbar.Right>
            {variant === "new" && <GridToolbar.SearchFilter />}
            {(isMlsAdministrator ||
              hasEmployeeRole([RoleEmployee.CompanyAdmin])) && (
              <GridToolbar.AddButton
                onClick={(): void => setShowAddModal(true)}
              />
            )}
            <GridToolbar.RefreshButton />
            {data.total > 0 && (
              <GridToolbar.ExportButtons
                sheetName="Users"
                onClick={fetchDataForDownloading}
                fileName="users"
              />
            )}
            <GridToolbar.ColumnVisibilityButton />
            {variant === "old" && (
              <>
                {(isMlsAdministrator ||
                  hasEmployeeRole([RoleEmployee.CompanyAdmin])) && (
                  <GridToolbar.SelectFilter
                    defaultValue={UserStatus.Active}
                    onChange={(value): void =>
                      setUserStatus(value as UserStatus)
                    }
                    options={statusOptions}
                    label="Status"
                  />
                )}
                <GridToolbar.SearchFilter />
              </>
            )}
          </GridToolbar.Right>
        </GridToolbar>
        <GridTable onRowClick={getUser} />
        <PersistentRightModal
          title="New User"
          open={showAddModal}
          onChange={(value): void => setShowAddModal(value)}
        >
          <UserCreateFormProvider>
            <UserCreateForm />
            <SaveButton getUsers={fetchData} />
          </UserCreateFormProvider>
        </PersistentRightModal>
      </GridProvider>
    </Fragment>
  );
}

export default UserGrid;
